import { getApiUrl } from '../utils/config';

/**
 * API Client for making HTTP requests
 */
class ApiClient {
  constructor() {
    this.baseUrl = getApiUrl();
  }

  /**
   * Get default headers
   * @returns {Object} Default headers
   */
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add authorization token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Handle API response
   * @param {Response} response - Fetch response
   * @returns {Promise} Parsed response data
   */
  async handleResponse(response) {
    let data;
    const contentType = response.headers.get('content-type');
    
    try {
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = text ? { error: { message: text } } : { error: { message: 'An error occurred' } };
      }
    } catch (parseError) {
      data = { error: { message: `Failed to parse response: ${parseError.message}` } };
    }

    if (!response.ok) {
      const error = {
        message: data.error?.message || `HTTP ${response.status}: ${response.statusText}`,
        code: data.error?.code || 'UNKNOWN_ERROR',
        status: response.status,
      };
      throw error;
    }

    return data;
  }

  /**
   * Make GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @param {Object} options - Additional options (responseType, etc.)
   * @returns {Promise} Response data
   */
  async get(endpoint, params = {}, options = {}) {
    // Ensure endpoint starts with /
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    
    // Construct full URL with origin for relative paths
    const fullUrl = this.baseUrl.startsWith('http') 
      ? `${this.baseUrl}${cleanEndpoint}`
      : `${window.location.origin}${this.baseUrl}${cleanEndpoint}`;
    
    const url = new URL(fullUrl);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });

    const headers = this.getHeaders();
    
    // Remove Content-Type for blob responses
    if (options.responseType === 'blob') {
      delete headers['Content-Type'];
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    // Handle blob responses
    if (options.responseType === 'blob') {
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'download';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, filename };
    }

    return this.handleResponse(response);
  }

  /**
   * Make POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @returns {Promise} Response data
   */
  async post(endpoint, data = {}) {
    // Ensure endpoint starts with /
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    
    // Construct full URL
    // If baseUrl is absolute (starts with http), use it directly
    // Otherwise, it's a relative path, prepend origin
    const fullUrl = this.baseUrl.startsWith('http') 
      ? `${this.baseUrl}${cleanEndpoint}`
      : `${window.location.origin}${this.baseUrl}${cleanEndpoint}`;
    
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse(response);
  }

  /**
   * Make PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @returns {Promise} Response data
   */
  async put(endpoint, data = {}) {
    // Ensure endpoint starts with /
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    
    // Construct full URL with origin for relative paths
    const fullUrl = this.baseUrl.startsWith('http') 
      ? `${this.baseUrl}${cleanEndpoint}`
      : `${window.location.origin}${this.baseUrl}${cleanEndpoint}`;
    
    const response = await fetch(fullUrl, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse(response);
  }

  /**
   * Make DELETE request
   * @param {string} endpoint - API endpoint
   * @returns {Promise} Response data
   */
  async delete(endpoint) {
    // Ensure endpoint starts with /
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    
    // Construct full URL with origin for relative paths
    const fullUrl = this.baseUrl.startsWith('http') 
      ? `${this.baseUrl}${cleanEndpoint}`
      : `${window.location.origin}${this.baseUrl}${cleanEndpoint}`;
    
    const response = await fetch(fullUrl, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    return this.handleResponse(response);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export health check function as example
export const healthService = {
  check: () => apiClient.get('/health'),
};
